/**
 * Simple API/Service to fetch Blogs data
 * @module Blogs-Service
 */

import { TypePageBlogDetail, TypePageBlogs } from "../app/@types/pageTypes";
import { publicUrlFor } from "../globals/common-functions";

/**
 * API URL 
 * Here we are fetching data from local JSON
 */
const _URL = publicUrlFor("data/data.json");

/**
 * Getting all data stuff for Template's Blogs page
 * 
 * @param {Function} completion Callback function to return data on fetch success
 */
export async function getBlogsData(completion: Function) {
    fetch(_URL)
        .then(response => response.json())
        .then(json => completion(transformToBlogs(json["blogs"])))
        .catch(error => console.error(error));
}

/**
 * Getting all data stuff for Template's BlogsWithLeftSidebar page
 * 
 * @param {Function} completion Callback function to return data on fetch success
 */
export async function getBlogsGridDataWithSidebar(completion: Function) {
    fetch(_URL)
        .then(response => response.json())
        .then(json => {
            const result = transformToBlogsSidebar(json["blogsSidebar"]);
            if (result.sidebar) {
                result.sidebar.news = json["blogs"]["blogs"]; // Use actual blog list
            }
            completion(result);
        })
        .catch(error => console.error(error));
}

/**
 * Getting all data stuff for Template's BlogsStandard page
 * 
 * @param {Function} completion Callback function to return data on fetch success
 */
export async function getBlogsStandardDataWithSidebar(completion: Function) {
    fetch(_URL)
        .then(response => response.json())
        .then(json => {
            const result = transformToBlogsSidebar(json["blogsStandardSidebar"]);
            if (result.sidebar) {
                result.sidebar.news = json["blogs"]["blogs"]; // Use actual blog list
            }
            completion(result);
        })
        .catch(error => console.error(error));
}

/**
 * Getting all data stuff for Template's BlogDetail page
 * 
 * @param {Function} completion Callback function to return data on fetch success
 * @param {string} blogId Optional blog ID to fetch specific blog detail
 */
export async function getBlogDetail(completion: Function, blogId?: string) {
    fetch(_URL)
        .then(response => response.json())
        .then(json => {
            if (blogId && json["blogDetails"] && json["blogDetails"][blogId]) {
                // Fetch specific blog detail by ID
                const blogDetail = json["blogDetails"][blogId];
                const result = {
                    content: blogDetail.content,
                    pagination: blogDetail.pagination,
                    author: json["blogDetailSidebar"]["author"],
                    sidebar: {
                        services: json["blogDetailSidebar"]["sidebar"]["services"] || [],
                        news: json["blogs"]["blogs"] // Use actual blog list instead of hardcoded news
                    },
                    comments: json["blogDetailSidebar"]["comments"]
                };
                completion(result);
            } else {
                // Fall back to default blog detail with real blog data
                const result = transformToBlogDetail(json["blogDetailSidebar"]);
                result.sidebar = {
                    services: result.sidebar?.services || [],
                    news: json["blogs"]["blogs"] // Use actual blog list instead of hardcoded news
                };
                completion(result);
            }
        })
        .catch(error => console.error(error));
}

/**
 * Function produces data object for Blogs using raw JSON data
 * @param {object} rawData raw JSON object
 * @returns Object of type Blogs
 */
const transformToBlogs = (rawData: any): TypePageBlogs => {
    // auto type casting
    var _blogs: TypePageBlogs = {};
    _blogs.blogs = rawData["blogs"];
    return _blogs;
}

/**
 * Function produces data object for Blogs using raw JSON data
 * @param {object} rawData raw JSON object
 * @returns Object of type Blogs
 */
const transformToBlogsSidebar = (rawData: any): TypePageBlogs => {
    // auto type casting
    var _blogs: TypePageBlogs = {};
    _blogs.blogs = rawData["blogs"];
    _blogs.sidebar = rawData["sidebar"];
    return _blogs;
}

/**
 * Function produces data object for Blogs using raw JSON data
 * @param {object} rawData raw JSON object
 * @returns Object of type Blogs
 */
const transformToBlogDetail = (rawData: any): TypePageBlogDetail => {
    // auto type casting
    var _detail: TypePageBlogDetail = {};
    _detail.content = rawData["content"];
    _detail.author = rawData["author"];
    _detail.pagination = rawData["pagination"];
    _detail.sidebar = rawData["sidebar"];
    _detail.comments = rawData["comments"];
    return _detail;
}